// WorkflowDesignerTaskNode.jsx

import React, { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  MISSION_TASK_HANDLED_BY,
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
  normalizeMissionPeople,
} from 'missions/taskmodal/missionTaskModalUtils';

// CSS
import './WorkflowDesignerTaskNode.css';

// Components
import Icon from 'svg/Icon';
import ProfileStack from 'components/profilestack/ProfileStack';

export const WORKFLOW_DESIGNER_SOURCE_HANDLE = 'out';

/**
 * WorkflowDesignerTaskNode
 *
 * Compact task card: title/settings on top, assignees (or People/Bot) + add on bottom.
 * Edges enter left and leave right from a shared source handle.
 *
 * @returns {ReactNode}
 */
const WorkflowDesignerTaskNode = memo(({ data, selected }) => {
  const { theme } = useTheme();
  const task = data?.task || {};
  const isStart = data?.isStart === true;
  const handledBy = String(task?.handledBy || '').trim().toLowerCase();
  const isBot = handledBy === MISSION_TASK_HANDLED_BY.BOT;
  const title = String(task?.title || '').trim() || 'Untitled task';

  const assigneeProfiles = useMemo(() => {
    if (isBot) return [];
    return normalizeMissionPeople(task?.assignees)
      .map((assignee) => {
        const key = getMissionTaskPersonKey(assignee);
        if (!key) return null;
        const label = getMissionTaskPersonLabel(assignee) || key;
        return {
          key,
          userKey: key,
          name: label,
          title: String(assignee?.title || '').trim() || label,
          coverUrl: assignee?.coverUrl || '',
          data_profile: {
            name: label,
            email: assignee?.email || assignee?.data_profile?.email || '',
          },
        };
      })
      .filter(Boolean);
  }, [isBot, task?.assignees]);

  const assigneeNameList = useMemo(() => (
    assigneeProfiles
      .map((profile) => profile.title || profile.name)
      .filter(Boolean)
      .join(', ')
  ), [assigneeProfiles]);

  return (
    <div
      className={[
        'workflow-designer-task-node',
        selected ? 'workflow-designer-task-node-selected' : '',
        isStart ? 'workflow-designer-task-node-start' : '',
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: theme.panel,
        borderColor: selected ? theme.blue : theme.outline,
        color: theme.foreground,
      }}
    >
      <Handle
        className="workflow-designer-task-node-target"
        position={Position.Left}
        type="target"
      />

      <div className="workflow-designer-task-node-header">
        <div className="workflow-designer-task-node-title-row">
          {isStart ? (
            <span
              className="workflow-designer-task-node-start-badge"
              style={{
                backgroundColor: theme.blue,
                color: theme.highlightForeground,
              }}
            >
              Start
            </span>
          ) : null}
          <span className="workflow-designer-task-node-title" title={title}>
            {title}
          </span>
        </div>

        <button
          className="workflow-designer-task-node-settings"
          title="Edit task"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            data?.onEditTask?.(task);
          }}
        >
          <Icon
            color={theme.foregroundMuted}
            height={16}
            iconKey="settings"
            width={16}
          />
        </button>
      </div>

      <div className="workflow-designer-task-node-footer">
        {isBot ? (
          <span
            className="workflow-designer-task-node-meta"
            style={{ color: theme.foregroundMuted }}
          >
            Bot
          </span>
        ) : assigneeProfiles.length > 0 ? (
          <div
            className="workflow-designer-task-node-assignees"
            title={assigneeNameList}
          >
            <ProfileStack
              allowNavigation={false}
              maxDisplay={3}
              overlap={6}
              profiles={assigneeProfiles}
              size={22}
            />
          </div>
        ) : (
          <span
            className="workflow-designer-task-node-meta"
            style={{ color: theme.foregroundMuted }}
          >
            People
          </span>
        )}

        <div
          className="workflow-designer-task-node-add-outcome"
          title="Continue to next task"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            data?.onAddOutcome?.(task);
          }}
        >
          <Icon
            color={theme.foreground}
            height={22}
            iconKey="add"
            width={22}
          />
        </div>
      </div>

      <Handle
        className="workflow-designer-task-node-source"
        id={WORKFLOW_DESIGNER_SOURCE_HANDLE}
        position={Position.Right}
        type="source"
      />
    </div>
  );
});

WorkflowDesignerTaskNode.displayName = 'WorkflowDesignerTaskNode';

export default WorkflowDesignerTaskNode;
